/**
 * Vulnerability Actions Component
 *
 * Action bar for bulk vulnerability operations including:
 * - Annotate (Note, Tag, Remove Tag)
 * - Scan (Rescan Affected Assets, Confirm Affiliation)
 * - Export (Always-enabled export button)
 * - Status/Severity changes (via standard action pattern)
 *
 * Layout uses justify-between to separate selection-dependent actions (left)
 * from always-enabled export (right).
 *
 * @module sections/vulnerabilities/components/actions
 */

import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/solid';
import { isAxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { AskMarcusButton } from '@/components/actions/AskMarcusButton';
import { VulnerabilityManagedDropdown } from '@/components/actions/VulnerabilityManagedDropdown';
import { Dropdown } from '@/components/Dropdown';
import {
  buildAnnotateAction,
  buildExportAction,
  buildGenericCustomAction,
  buildImportAction,
  buildScanActionHandlers,
  buildShareAction,
  TableActionsBar,
} from '@/components/table/actions';
import { Button } from '@/components/ui/button';
import { shouldUseBulk } from '@/hooks/useBulkAction/useBulkAction';
import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useMy } from '@/hooks/useMy';
import { useSummonAurelius } from '@/hooks/useSummonAurelius';
import { RetestButton } from '@/sections/vulnerabilities/components/actions';
import { HackerOneSubmitModal } from '@/sections/vulnerabilities/components/modals/HackerOneSubmitModal';
import {
  canValidate,
  getExploitButtonConfig,
  getExploitState,
  getPatchButtonConfig,
  getPatchState,
  getValidateButtonConfig,
  getValidateState,
  isConstantineFinding,
  useExploitVerification,
  usePatchGeneration,
  useValidateVerification,
  useVulnerabilityActionHandlers,
} from '@/sections/vulnerabilities/hooks/actions';
import { useAuth } from '@/state/auth';
import { Risk } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';
import { allHuntFindings, canRescanAnyRisk } from '@/utils/risk.util';

/**
 * Props for VulnerabilityActions component
 */
export interface VulnerabilityActionsProps {
  /** Vulnerabilities to perform actions on */
  vulnerabilities: Risk[];
  /** Additional CSS classes */
  className?: string;
  /** Hide the "Actions:" label */
  hideLabel?: boolean;
  /** Handler for opening tag modal */
  onTagClick?: () => void;
  /** Handler for opening note modal */
  onNoteClick?: () => void;
  /** Handler for opening remove tag modal */
  onRemoveTagClick?: () => void;
  /** Handler for opening import modal */
  onImportClick: () => void;
  /** Handler for opening export modal */
  onExportClick: () => void;
  /** Handler for opening auxiliary info modal */
  onAuxInfoClick?: () => void;
  /** Handler for refreshing visited timestamp (Praetorian users only) */
  onRefreshVisited?: (comment: string) => void;
  /** Whether the refresh visited mutation is in progress */
  isRefreshingVisited?: boolean;
  /** Additional actions for "Ticketing" dropdown (Jira ticket actions) */
  moreActions?: DropdownOption[];
  /** PlexTrac-specific actions dropdown (Praetorian users only) */
  plexTracActions?: DropdownOption[];
  /** Handler for risk status update */
  onUpdateRisk?: (risk: Risk) => Promise<void>;
  /** Handler for clearing selection after actions */
  onClearSelection?: () => void;
  /**
   * Current bulk-selection state. When provided and shouldUseBulk() returns
   * true, the Manage dropdown intercepts at this boundary and calls
   * onBulkStatusChange instead of per-item onUpdateRisk.
   */
  selectionState?: BulkSelectionState;
  /**
   * Called ONCE when bulk mode is active and the user picks a new status (or reject).
   * Receives the lifecycle state char (e.g. 'O', 'D') and an optional substate
   * (e.g. 'FalsePositive'). Severity is NOT included — the backend preserves it
   * from the existing risk atomically.
   */
  onBulkStatusChange?: (state: string, substate?: string) => void;
  /**
   * Called ONCE when bulk mode is active and the user picks a new severity.
   * Receives the severity char. Caller opens severityConfirm.openConfirmation().
   */
  onBulkSeverityChange?: (severityChar: string) => void;
  /**
   * Called ONCE when bulk mode is active (explicit selection) and the user picks
   * "Delete Permanently". Caller opens the delete bulk-confirmation dialog.
   * Praetorian-only; explicit-selection-only + capped (amendment A2).
   */
  onBulkDeleteRequested?: () => void;
  /**
   * Called for the single / small-N "Delete Permanently" path with the selected
   * risks. Caller opens a destructive ConfirmationDialog. Praetorian-only.
   */
  onHardDelete?: (risks: Risk[]) => void;
  /** Handler called after scan actions complete */
  onScanComplete?: () => void;
  /** Severity filter options for dropdown */
  severityOptions?: Array<{
    label: string;
    value: string;
    id: string;
    severity?: string;
  }>;
  /**
   * Write-legal attack-surface options. Passed ONLY from the drawer when the
   * Praetorian edit gate is open (see VulnerabilityDrawer); omitted in the
   * table context so the "Change Surface" submenu never renders there.
   */
  attackSurfaceOptions?: Array<{ label: string; value: string }>;
  /** Function to get risk status label */
  getRiskStatusLabel?: (status: string) => { status: string; severity: string };
  /** Hide all action buttons */
  hideActions?: boolean;
  /** Disable actions when no items selected */
  disabled?: boolean;
  /**
   * Open-state for the HackerOne submit modal. When supplied, the parent
   * (sections/vulnerabilities/index.tsx) owns this state so it can participate
   * in anyVulnerabilityDialogOpen and gate the table's row-action hotkeys.
   * Optional — consumers that don't gate hotkeys (e.g. the drawer) can omit it
   * and the component falls back to internal state.
   */
  isHackerOneModalOpen?: boolean;
  setIsHackerOneModalOpen?: (open: boolean) => void;
}

/**
 * VulnerabilityActions Component
 *
 * Renders action dropdown buttons for selected vulnerabilities.
 * Provides consistent action interface across the vulnerabilities section.
 *
 * @param props - Component props
 * @returns Action bar JSX
 *
 * @example
 * ```tsx
 * <VulnerabilityActions
 *   vulnerabilities={selectedRisks}
 *   onNoteClick={() => setShowNoteModal(true)}
 *   onTagClick={() => setShowTagModal(true)}
 *   onRemoveTagClick={() => setShowRemoveTagModal(true)}
 *   onExportClick={() => setShowExportModal(true)}
 *   onUpdateRisk={handleUpdateRisk}
 *   onClearSelection={clearSelection}
 *   severityOptions={severityFilters}
 *   getRiskStatusLabel={getRiskStatusLabel}
 * />
 * ```
 */
export function VulnerabilityActions({
  vulnerabilities,
  className,
  hideLabel,
  onNoteClick,
  onTagClick,
  onRemoveTagClick,
  onImportClick,
  onExportClick,
  onAuxInfoClick,
  onRefreshVisited,
  isRefreshingVisited = false,
  moreActions,
  plexTracActions,
  onUpdateRisk,
  onClearSelection,
  onScanComplete,
  selectionState,
  onBulkStatusChange,
  onBulkSeverityChange,
  onBulkDeleteRequested,
  onHardDelete,
  severityOptions = [],
  attackSurfaceOptions,
  getRiskStatusLabel,
  hideActions,
  disabled = false,
  isHackerOneModalOpen: isHackerOneModalOpenProp,
  setIsHackerOneModalOpen: setIsHackerOneModalOpenProp,
}: VulnerabilityActionsProps) {
  const handlers = useVulnerabilityActionHandlers(
    vulnerabilities,
    onScanComplete
  );
  const { isPraetorianUser } = useAuth();
  const { summonAurelius } = useSummonAurelius();
  const { canWriteAssets, canWriteJobs } = useEntitlements();
  const { data: accounts = [] } = useMy({ resource: 'account' });
  const hackerOneAccount = accounts.find(
    (a: { member: string }) => a.member === 'hackerone'
  );
  const hackerOneProgramUrl = hackerOneAccount?.value
    ? `https://hackerone.com/${hackerOneAccount.value}`
    : '';
  const {
    handleExploit,
    isRunning: isExploitRunning,
    stopPolling,
  } = useExploitVerification(onScanComplete);
  const {
    handleValidate,
    isRunning: isValidateRunning,
    stopPolling: stopValidatePolling,
  } = useValidateVerification(onScanComplete);
  const {
    handlePatch,
    isRunning: isPatchRunning,
    stopPolling: stopPatchPolling,
  } = usePatchGeneration(onScanComplete);

  const allConstantine =
    vulnerabilities.length > 0 && vulnerabilities.every(isConstantineFinding);
  const riskExploitState = allConstantine
    ? getExploitState(vulnerabilities)
    : 'ready';
  const exploitState = isExploitRunning ? 'running' : riskExploitState;

  // Stop polling when exploit results arrive on the risks
  useEffect(() => {
    if (
      isExploitRunning &&
      (riskExploitState === 'exploitable' ||
        riskExploitState === 'not_exploitable' ||
        riskExploitState === 'inconclusive')
    ) {
      stopPolling();
    }
  }, [isExploitRunning, riskExploitState, stopPolling]);

  // Stop polling when patch results arrive on the risks
  const riskPatchState = allConstantine
    ? getPatchState(vulnerabilities)
    : 'ready';
  useEffect(() => {
    if (
      isPatchRunning &&
      (riskPatchState === 'patched' || riskPatchState === 'patch_failed')
    ) {
      stopPatchPolling();
    }
  }, [isPatchRunning, riskPatchState, stopPatchPolling]);

  // Stop polling when validate results arrive on the risks
  const riskValidateState = vulnerabilities.some(canValidate)
    ? getValidateState(vulnerabilities.filter(canValidate))
    : 'ready';
  useEffect(() => {
    if (
      isValidateRunning &&
      (riskValidateState === 'validated' ||
        riskValidateState === 'insufficient' ||
        riskValidateState === 'inconclusive')
    ) {
      stopValidatePolling();
    }
  }, [isValidateRunning, riskValidateState, stopValidatePolling]);

  // HackerOne modal open-state is controlled by the parent when provided
  // (so it can gate table hotkeys); otherwise fall back to internal state.
  const [internalHackerOneOpen, setInternalHackerOneOpen] = useState(false);
  const isHackerOneModalOpen =
    isHackerOneModalOpenProp ?? internalHackerOneOpen;
  const setIsHackerOneModalOpen =
    setIsHackerOneModalOpenProp ?? setInternalHackerOneOpen;

  // When every selected risk is a hunt finding (agent-reported), the rescan
  // action becomes "Rescan with Marcus" and dispatches cato-agent instead of
  // re-running an originating capability that doesn't exist for these risks.
  const allRescanWithMarcus = allHuntFindings(vulnerabilities);

  // Severity change handler with proper typing
  const handleSeverityChange = useMemo(
    () =>
      (option: {
        value?: string | number;
        label: string;
        severity?: string;
      }) => {
        if (!option?.value) return;

        // Bulk intercept: route through bulk confirmation once.
        if (
          selectionState &&
          shouldUseBulk(selectionState) &&
          onBulkSeverityChange
        ) {
          onBulkSeverityChange(String(option.value));
          return;
        }

        if (!onUpdateRisk || !getRiskStatusLabel) return;

        // Single-row path: update each item then clear selection once.
        const promises = vulnerabilities.map(risk => {
          const currentStatus = getRiskStatusLabel(risk.status).status;
          const newSeverity = String(option.value);
          const substatus = risk.status.slice(2);
          const combinedStatus = `${currentStatus}${newSeverity}${substatus}`;
          return onUpdateRisk({
            ...risk,
            status: combinedStatus,
          });
        });
        Promise.allSettled(promises).then(results => {
          const rejected = results.filter(r => r.status === 'rejected');
          if (rejected.length > 0) {
            console.error(
              `Severity change: ${rejected.length} item(s) failed`,
              rejected
            );
            const hasTimeout = rejected.some(r => {
              const reason = (r as PromiseRejectedResult).reason;
              return (
                isAxiosError(reason) &&
                (reason.response?.status === 504 ||
                  reason.response?.status === 408)
              );
            });
            toast.error('Failed to change severity', {
              description: hasTimeout
                ? 'The request may still be processing. Check the status before retrying.'
                : 'Please try again. If the problem persists, contact support@praetorian.com.',
              duration: 8000,
            });
          }
          if (rejected.length < results.length) {
            onClearSelection?.();
          }
        });
      },
    [
      vulnerabilities,
      onUpdateRisk,
      getRiskStatusLabel,
      onClearSelection,
      selectionState,
      onBulkSeverityChange,
    ]
  );

  // Attack-surface change handler (single-select-replace). Always sends
  // exactly one value — never an empty array — so the backend Merge can't
  // clear the surface (security finding F2). No bulk branch: the drawer is the
  // only caller and passes a single-risk array. Errors and selection-clear
  // mirror the severity handler for consistent UX.
  const handleAttackSurfaceChange = (value: string) => {
    if (!onUpdateRisk || !value) return;

    const promises = vulnerabilities.map(risk =>
      onUpdateRisk({ ...risk, attackSurface: [value] })
    );
    Promise.allSettled(promises).then(results => {
      const rejected = results.filter(r => r.status === 'rejected');
      if (rejected.length > 0) {
        console.error(
          `Attack surface change: ${rejected.length} item(s) failed`,
          rejected
        );
        const hasTimeout = rejected.some(r => {
          const reason = (r as PromiseRejectedResult).reason;
          return (
            isAxiosError(reason) &&
            (reason.response?.status === 504 || reason.response?.status === 408)
          );
        });
        toast.error('Failed to change attack surface', {
          description: hasTimeout
            ? 'The request may still be processing. Check the status before retrying.'
            : 'Please try again. If the problem persists, contact support@praetorian.com.',
          duration: 8000,
        });
      }
      if (rejected.length < results.length) {
        onClearSelection?.();
      }
    });
  };

  // Build action configurations
  const actions = [
    buildGenericCustomAction({
      render: () => (
        <AskMarcusButton
          onClick={() =>
            summonAurelius({
              type: 'vulnerability',
              keys: vulnerabilities.map(v => v.key),
              displayNames: vulnerabilities.map(v => v.name),
            })
          }
          disabled={disabled}
          data-testid="vulnerability-ask-marcus-button"
        />
      ),
      position: 'first',
      testId: 'vulnerability-ask-marcus-wrapper',
    }),

    // RetestButton triggers a retest via POST /job/ (useRetestRisk), which is
    // protected by the write_jobs entitlement — NOT write_assets. Gate it on
    // canWriteJobs to match the sibling job actions (Rescan with Marcus, etc.)
    // so RBAC tenants with write_assets but not write_jobs don't see an enabled
    // button that can never succeed.
    ...(canWriteJobs
      ? [
          buildGenericCustomAction({
            render: () => <RetestButton vulnerabilities={vulnerabilities} />,
            position: 'first',
            testId: 'vulnerability-retest-wrapper',
          }),
        ]
      : []),

    ...(canWriteAssets
      ? [
          buildAnnotateAction({
            onCommentClick: onNoteClick || (() => {}),
            onTagClick: onTagClick || (() => {}),
            onRemoveTagClick: onRemoveTagClick || (() => {}),
            labels: { comment: 'Add Note' },
            testId: 'vulnerability-annotate-dropdown',
          }),
        ]
      : []),

    // Manage dropdown combining Status, Severity, and Aux Info (after Annotate)
    // Uses type: 'Manage' for proper action ordering (after Annotate, before Scan)
    ...(canWriteAssets && (onUpdateRisk || onAuxInfoClick)
      ? [
          {
            type: 'Manage' as const,
            // Stub handlers satisfy ManageActionConfig type; render() overrides rendering
            handlers: {
              onFreezeClick: () => {},
              onActivateClick: () => {},
              onDeleteClick: () => {},
            },
            render: () => (
              <VulnerabilityManagedDropdown
                risks={vulnerabilities}
                onUpdateRisk={
                  onUpdateRisk
                    ? async (risk, _comment) => {
                        // Single-row path only. Bulk is intercepted inside the dropdown
                        // before this callback is ever reached.
                        await onUpdateRisk(risk);
                        onClearSelection?.(); // Major 8 fix: restore selection clear
                      }
                    : undefined
                }
                selectionState={selectionState}
                onBulkStatusRequested={selectedValue => {
                  onBulkStatusChange?.(selectedValue);
                }}
                onBulkRejectRequested={combinedStatus => {
                  // combinedStatus is 'D{substatus}', e.g. 'DFalsePositive'.
                  // Decompose into state + substate for the bulk endpoint.
                  onBulkStatusChange?.(
                    combinedStatus[0],
                    combinedStatus.slice(1) || undefined
                  );
                }}
                onBulkDeleteRequested={onBulkDeleteRequested}
                onHardDelete={onHardDelete}
                getRiskStatusLabel={getRiskStatusLabel}
                severityOptions={severityOptions}
                onSeverityChange={handleSeverityChange}
                attackSurfaceOptions={attackSurfaceOptions}
                onAttackSurfaceChange={
                  attackSurfaceOptions ? handleAttackSurfaceChange : undefined
                }
                onAuxInfoClick={onAuxInfoClick}
                onRefreshVisited={onRefreshVisited}
                isRefreshingVisited={isRefreshingVisited}
                disabled={disabled}
                data-testid="vulnerability-manage-dropdown"
                label="Manage"
                icon={<EllipsisHorizontalCircleIcon className="size-4" />}
              />
            ),
            testId: 'vulnerability-manage-wrapper',
          },
        ]
      : []),

    ...(canWriteJobs
      ? [
          buildScanActionHandlers({
            handlers: {
              // Hunt findings (filed by an agent via report_new_risk) have no
              // originating capability to re-run — the agent that re-examines
              // them is cato-agent — so a pure hunt selection swaps the action
              // over to "Rescan with Marcus". Mixed/capability selections keep
              // the standard "Rescan Affected Assets" behavior.
              onRescanClick: allRescanWithMarcus
                ? () => handlers.handleRescanWithMarcus(vulnerabilities)
                : () => handlers.handleRescan(vulnerabilities),
            },
            labels: allRescanWithMarcus
              ? { rescan: 'Rescan with Marcus' }
              : undefined,
            // Standard rescan re-runs the originating capability; manual risks
            // have no capability to re-run, so hide it when every selected risk
            // is manual. Hunt findings always get the "Rescan with Marcus"
            // action, so never hide in that case.
            hideActions: {
              rescan:
                vulnerabilities.length > 0 &&
                !allRescanWithMarcus &&
                !canRescanAnyRisk(vulnerabilities),
            },
            testId: 'vulnerability-scan-dropdown',
          }),
        ]
      : []),

    // Exploit verification — only for Constantine findings, Praetorian users only
    ...(canWriteJobs && allConstantine && isPraetorianUser
      ? [
          buildGenericCustomAction({
            render: () => {
              const buttonConfig = getExploitButtonConfig(exploitState);
              const isCompleted =
                exploitState === 'exploitable' ||
                exploitState === 'not_exploitable' ||
                exploitState === 'inconclusive';
              return (
                <Button
                  onClick={() => handleExploit(vulnerabilities)}
                  disabled={
                    disabled || exploitState === 'running' || isCompleted
                  }
                  size="sm"
                  className={cn(
                    'rounded-full',
                    buttonConfig.className,
                    isCompleted && 'cursor-default'
                  )}
                  data-testid="vulnerability-exploit-button"
                >
                  {buttonConfig.icon}
                  {buttonConfig.label}
                </Button>
              );
            },
            testId: 'vulnerability-exploit-wrapper',
          }),
        ]
      : []),

    // Patch generation — only for Constantine findings, Praetorian users only
    ...(canWriteJobs && allConstantine && isPraetorianUser
      ? [
          buildGenericCustomAction({
            render: () => {
              const patchState = isPatchRunning
                ? 'running'
                : getPatchState(vulnerabilities);
              const buttonConfig = getPatchButtonConfig(patchState);
              const isCompleted =
                patchState === 'patched' || patchState === 'patch_failed';
              return (
                <Button
                  onClick={() => handlePatch(vulnerabilities)}
                  disabled={disabled || patchState === 'running' || isCompleted}
                  size="sm"
                  title={buttonConfig.tooltip}
                  className={cn(
                    'rounded-full',
                    buttonConfig.className,
                    isCompleted && 'cursor-default'
                  )}
                  data-testid="vulnerability-patch-button"
                >
                  {buttonConfig.icon}
                  {buttonConfig.label}
                </Button>
              );
            },
            testId: 'vulnerability-patch-wrapper',
          }),
        ]
      : []),

    // Validate Patch — only visible when exploit has succeeded, Praetorian users only
    ...(canWriteJobs && isPraetorianUser && vulnerabilities.some(canValidate)
      ? [
          buildGenericCustomAction({
            render: () => {
              const validateState = isValidateRunning
                ? 'running'
                : getValidateState(vulnerabilities.filter(canValidate));
              const validateConfig = getValidateButtonConfig(validateState);
              const isCompleted =
                validateState === 'validated' ||
                validateState === 'insufficient' ||
                validateState === 'inconclusive';
              return (
                <Button
                  onClick={() => handleValidate(vulnerabilities)}
                  disabled={
                    disabled ||
                    isValidateRunning ||
                    isExploitRunning ||
                    isCompleted
                  }
                  size="sm"
                  className={cn(
                    'rounded-full',
                    validateConfig.className,
                    isCompleted && 'cursor-default'
                  )}
                  data-testid="vulnerability-validate-button"
                >
                  {validateConfig.icon}
                  {validateConfig.label}
                </Button>
              );
            },
            testId: 'vulnerability-validate-wrapper',
          }),
        ]
      : []),

    ...(isPraetorianUser
      ? [
          buildGenericCustomAction({
            render: () => (
              <Dropdown
                placeholder="HackerOne"
                options={[
                  {
                    label: 'Submit to HackerOne',
                    value: 'hackerone',
                    onClick: () => setIsHackerOneModalOpen(true),
                  },
                ]}
                styleType="rounded"
                size="sm"
                className="[&_span:nth-child(3)]:hidden"
                listClassName="right-0"
                icon={<EllipsisHorizontalCircleIcon className="size-4" />}
                data-testid="vulnerability-hackerone-dropdown"
              />
            ),
            testId: 'vulnerability-hackerone-wrapper',
          }),
        ]
      : []),

    buildShareAction({
      variant: 'button-only',
      onShareClick: handlers.handleShare,
      testId: 'vulnerability-share-button',
    }),

    ...(canWriteAssets
      ? [
          buildImportAction({
            onImportClick,
            disabled: false, // Always enabled
            testId: 'vulnerability-import-button',
          }),
        ]
      : []),

    buildExportAction({
      onExportClick,
      disabled: disabled,
      alwaysEnabled: false,
      testId: 'vulnerability-export-button',
    }),

    ...(canWriteAssets && plexTracActions && plexTracActions.length > 0
      ? [
          buildGenericCustomAction({
            render: () => (
              <Dropdown
                placeholder="PlexTrac"
                options={plexTracActions}
                styleType="rounded"
                size="sm"
                className="[&_span:nth-child(3)]:hidden"
                listClassName="right-0"
                icon={<EllipsisHorizontalCircleIcon className="size-4" />}
                data-testid="vulnerability-plextrac-dropdown"
              />
            ),
            testId: 'vulnerability-plextrac-wrapper',
          }),
        ]
      : []),

    ...(canWriteAssets && moreActions && moreActions.length > 0
      ? [
          buildGenericCustomAction({
            render: () => (
              <Dropdown
                placeholder="Ticketing"
                options={moreActions}
                styleType="rounded"
                size="sm"
                className="[&_span:nth-child(3)]:hidden"
                listClassName="right-0"
                icon={<EllipsisHorizontalCircleIcon className="size-4" />}
                data-testid="vulnerability-ticketing-dropdown"
              />
            ),
            testId: 'vulnerability-ticketing-wrapper',
          }),
        ]
      : []),
  ];

  return (
    <>
      <TableActionsBar
        selectedItems={vulnerabilities}
        actions={actions}
        disabled={disabled}
        className={className}
        hideLabel={hideLabel}
        hideActions={hideActions}
        testIdPrefix="vulnerability"
      />
      {isPraetorianUser && (
        <HackerOneSubmitModal
          open={isHackerOneModalOpen}
          onClose={() => setIsHackerOneModalOpen(false)}
          vulnerabilities={vulnerabilities}
          programUrl={hackerOneProgramUrl}
          onSubmit={async params => {
            await handlers.handleSubmitToHackerOne(vulnerabilities, params);
            setIsHackerOneModalOpen(false);
          }}
        />
      )}
    </>
  );
}
